// A Remote Relay pairing link (`#relay=<params>` on any page, the pull
// request's own page included, see docs/remote-relay.md) opened in this tab.
// The app takes it off the URL at startup, before the sign-in gate, and
// holds it in this tab until a page takes it, so it survives signing in
// (whose return address carries no fragment). Only the fragment is removed:
// the path and query stay.

const KEY = 'changeling:relay-invite';
/** Told on the window when a link is captured, for Settings already shown. */
export const INVITE_CAPTURED = 'changeling:relay-invite';
const PREFIX = '#relay=';
/** The invitation's own life: older ones are refused by the agent anyway. */
export const INVITE_TTL_MS = 10 * 60 * 1000;

interface Held {
  params: string;
  at: number;
}

/** Kept here too, for a tab whose sessionStorage throws. */
let held: Held | null = null;

/**
 * Take a pairing link's parameters off the address bar and hold them. This
 * replaces the current session-history entry; the browser's global history
 * still has the visited URL, which is why the invitation expires and binds
 * to the first browser that redeems it.
 */
export function captureInvite(now = Date.now()): void {
  if (!location.hash.startsWith(PREFIX)) return;
  held = { params: location.hash.slice(PREFIX.length), at: now };
  history.replaceState(history.state, '', location.pathname + location.search);
  try {
    sessionStorage.setItem(KEY, JSON.stringify(held));
  } catch {
    // Held in memory: it is lost on reload, as the link would be.
  }
  window.dispatchEvent(new Event(INVITE_CAPTURED));
}

/** Capture now, and each time a link is opened in the tab without a reload (a fragment change). */
export function installInviteCapture(): void {
  captureInvite();
  window.addEventListener('hashchange', () => captureInvite());
}

/**
 * The held link's (URI-encoded) parameters, once: `expired` when it is older
 * than an invitation lives, `null` when there is none.
 */
export function takeInvite(now = Date.now()): string | 'expired' | null {
  let found = held;
  held = null;
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (raw) found = JSON.parse(raw) as Held;
  } catch {
    // Only the one in memory, if any.
  }
  if (!found || typeof found.params !== 'string' || typeof found.at !== 'number') return null;
  return now - found.at < INVITE_TTL_MS ? found.params : 'expired';
}
